import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Shield,
  Radio,
  Lock,
  Volume2,
  Lightbulb,
  Zap
} from "lucide-react";

export default function Services() {
  const { t } = useTranslation();

  const services = [
    {
      key: "smartHomeAutomation",
      path: "/services/smart-home",
      icon: Home,
      color: "#4f46e5"
    },
    {
      key: "securityAndSurveillanceSystem",
      path: "/services/security",
      icon: Shield,
      color: "#dc2626"
    },
    {
      key: "intercomSystem",
      path: "/services/intercom",
      icon: Radio,
      color: "#059669"
    },
    {
      key: "smartLockAndAccessControlSystems",
      path: "/services/smart-lock",
      icon: Lock,
      color: "#d97706"
    },
    {
      key: "soundSystems",
      path: "/services/sound",
      icon: Volume2,
      color: "#7c3aed"
    },
    {
      key: "lighteningSolutions",
      path: "/services/lightening",
      icon: Lightbulb,
      color: "#eab308"
    },
    {
      key: "electricalWiringAccessories",
      path: "/services/electrical",
      icon: Zap,
      color: "#0891b2"
    }
  ];

  return (
    <div
      className="min-h-screen py-16 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(135deg, #fafcf0 0%, #f5f7ed 100%)'
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1
            className="text-5xl font-bold mb-4"
            style={{ color: '#2d2d2b' }}
          >
            {t("services")}
          </h1>
          <div
            className="w-24 h-1.5 mx-auto rounded-full"
            style={{ background: '#ffde21' }}
          />
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <Link
                key={service.key}
                to={service.path}
                className="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{
                  background: 'rgba(250, 252, 240, 0.95)',
                  boxShadow: '0 4px 20px rgba(45, 45, 43, 0.1)',
                  border: '2px solid rgba(255, 222, 33, 0.2)'
                }}
              >
                {/* Card Content */}
                <div className="p-8">
                  {/* Icon Container */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{
                      background: `${service.color}15`,
                      border: `2px solid ${service.color}30`
                    }}
                  >
                    <Icon
                      size={32}
                      style={{ color: service.color }}
                      className="transition-transform duration-300 group-hover:rotate-12"
                    />
                  </div>

                  {/* Service Title */}
                  <h3
                    className="text-xl font-bold mb-3 transition-colors duration-300"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(service.key)}
                  </h3>

                  {/* Arrow Icon */}
                  <div
                    className="flex items-center gap-2 font-semibold transition-all duration-300 group-hover:gap-4"
                    style={{ color: service.color }}
                  >
                    <span className="text-sm">{t("homepage.learnMore")}</span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    >
                      <path
                        d="M4 10h12m0 0l-4-4m4 4l-4 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Hover Effect Background */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: `linear-gradient(135deg, ${service.color}08 0%, ${service.color}15 100%)`
                  }}
                />

                {/* Yellow Accent on Hover */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                  style={{ background: '#ffde21' }}
                />
              </Link>
            );
          })}
        </div>

        {/* Call to Action Section */}
        <div
          className="mt-20 text-center p-12 rounded-3xl relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 222, 33, 0.15) 0%, rgba(255, 222, 33, 0.05) 100%)',
            border: '2px solid rgba(255, 222, 33, 0.3)'
          }}
        >
          <h2
            className="text-3xl font-bold mb-4"
            style={{ color: '#2d2d2b' }}
          >
            {t("homepage.services.need_custom_solution")}
          </h2>
          <p
            className="text-lg mb-8 max-w-2xl mx-auto"
            style={{ color: '#2d2d2b', opacity: 0.8 }}
          >
            {t("homepage.services.cta_message")}
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
            style={{
              background: '#ffde21',
              color: '#2d2d2b'
            }}
          >
            {t("contact.title")}
          </Link>
        </div>
      </div>
    </div>
  );
}